import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { useSelector } from 'react-redux';
import { SimpleInput } from 'app/shared/input';
import { MultiSelect } from 'app/shared/select';
import { Button } from 'app/shared/button';
import { DetailItem } from 'app/shared/info-section/item';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { Spacer } from 'app/layouts/generic';
import { toReadableNumber } from 'app/shared/utils/number';
import { useModuleService } from 'hooks/sales/module';
import { useInvoiceService } from 'hooks/sales/invoice';
import { useCurrencyService } from 'hooks/sales/currency';
import { useTaxService } from 'hooks/sales/tax';
import { InfoBanner } from 'app/shared/info-banner';
import { toast } from 'react-toastify';
import styles from './invoice.module.css';

const DEFAULT_CURRENCY = 'NGN';

const RENEWAL_PERIODS = [
  { label: 'Monthly', value: 'month' },
  { label: 'Annually', value: 'year' }
];

// Minimum margin as a fraction of the plan price.
const minMarginRate = (renewal, tier) => {
  if (renewal === 'month') return 0.05;
  if (tier === 'enterprise') return 0.15;
  if (tier === 'professional') return 0.1;
  return 0.05;
};

const bracketsFromPlan = (plan) =>
  (plan?.pricing || [])
    .map((pricing) => ({
      min: pricing.contact_bracket.min,
      max: pricing.contact_bracket.max,
      value: `${pricing.contact_bracket.min}:${pricing.contact_bracket.max}`,
      label: `${toReadableNumber(pricing.contact_bracket.min)} - ${toReadableNumber(
        pricing.contact_bracket.max
      )}`
    }))
    .sort((a, b) => Number(a.min) - Number(b.min));

export const RaiseInvoiceModal = ({
  organization = {},
  onRaised = () => {},
  closeModal = () => {}
}) => {
  const agency = useSelector((state) => state.metadata.agency);
  const { fetchModules } = useModuleService();
  const { raiseInvoice } = useInvoiceService();
  const { fetchCurrencies, getCurrencySymbol } = useCurrencyService();
  const { fetchTaxes } = useTaxService();

  const [tax_rates, setTaxRates] = useState({});
  const [file_location, setFileLocation] = useState('');
  const [modules, setModules] = useState([]);
  const [selected_plan, setSelectedPlan] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [selected_currency, setSelectedCurrency] = useState(null);
  const [contact_brackets, setContactBrackets] = useState([]);
  const [selected_bracket, setSelectedBracket] = useState(null);
  const [selected_renewal, setSelectedRenewal] = useState(RENEWAL_PERIODS[0]);
  const [due_date, setDueDate] = useState('');
  const [margin, setMargin] = useState('');
  const [showing_summary, setShowingSummary] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchModules().then(({ modules: fetched }) => {
      const sorted = (fetched || []).sort((a, b) => a.tier_index - b.tier_index);
      const options = sorted.map((module) => ({
        ...module,
        label: module.title,
        value: module.tier
      }));
      setModules(options);
      if (options.length) setSelectedPlan(options[0]);
    });

    fetchCurrencies().then(({ currencies: fetched }) => {
      const options = (fetched || []).map((currency) => ({
        ...currency,
        value: currency.code,
        label: currency.code
      }));
      setCurrencies(options);
      setSelectedCurrency(options.find((c) => c.code === DEFAULT_CURRENCY) || options[0] || null);
    });

    fetchTaxes().then(({ taxes }) => {
      setTaxRates((taxes || []).reduce((map, rate) => ({ ...map, [rate.currency]: rate.tax }), {}));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh contact-bracket options whenever the plan changes.
  useEffect(() => {
    const brackets = bracketsFromPlan(selected_plan);
    setContactBrackets(brackets);
    setSelectedBracket(brackets[0] || null);
  }, [selected_plan]);

  // Start fresh each time the modal opens for a different organisation.
  useEffect(() => {
    if (!organization?.id) return;
    setFileLocation('');
    setShowingSummary(false);
    setDueDate('');
    setMargin('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.id]);

  const target_pricing = (selected_plan?.pricing || []).find(
    (pricing) =>
      selected_bracket &&
      pricing.contact_bracket.min === selected_bracket.min &&
      pricing.contact_bracket.max === selected_bracket.max
  );

  const currency_code = selected_currency?.code;
  const currency_symbol = getCurrencySymbol(currency_code) || '';

  const renewal = selected_renewal.value;
  const base_amount =
    target_pricing?.prices?.find((p) => p.currency === currency_code)?.amount || 0;
  const gross_price = renewal === 'year' ? base_amount * 12 : base_amount;
  // The explorer plan is never discounted; every other plan uses its standard discount.
  const discount_rate =
    selected_plan?.tier === 'explorer' ? 0 : target_pricing?.discounts?.[renewal]?.value || 0;
  const plan_price = Math.round(gross_price - (gross_price * discount_rate) / 100);

  const min_margin = Math.ceil(plan_price * minMarginRate(renewal, selected_plan?.tier));
  const margin_value = Number(margin);

  const margin_error =
    margin !== '' && margin_value < min_margin
      ? `Minimum margin is ${currency_symbol}${toReadableNumber(min_margin)}.`
      : '';

  const is_valid =
    !!selected_plan &&
    !!selected_currency &&
    !!selected_bracket &&
    !!selected_renewal &&
    !!due_date &&
    margin_value > 0 &&
    margin_value >= min_margin;

  const discount_amount = gross_price - plan_price;
  const subtotal = plan_price + margin_value;
  const tax_rate = tax_rates[currency_code] || 0;
  const vat = Math.round((subtotal * tax_rate) / 100);
  const total = subtotal + vat;
  const money = (amount) => `${currency_symbol}${toReadableNumber(amount)}`;

  // Show the breakdown before generating the invoice.
  const review = () => {
    if (!is_valid) return;
    if (!organization.address || !organization.address.street) {
      return toast.error(
        'Please provide a street address for the organization before raising an invoice.'
      );
    }
    setShowingSummary(true);
  };

  const submit = async () => {
    if (!is_valid) return;
    const item = {
      product: {
        ...selected_plan
      },
      pricing: target_pricing,
      discount: {
        rate: discount_rate,
        amount: gross_price - plan_price
      },
      cost: Number(plan_price) + Number(margin_value),
      period: selected_renewal.value,
      units: 0
    };

    const data = {
      agent: {
        business_name: agency?.business_name,
        firstname: agency?.firstname,
        lastname: agency?.lastname,
        email: agency?.email,
        id: agency?.id,
        address: agency?.address,
        track: agency?.track
      },
      agent_id: agency?.id,
      agent_bvn: agency?.bvn,
      tenant: organization,
      items: [item],
      currency: currency_code,
      due_date,
      gross_price,
      margin_value,
      subtotal,
      vat,
      vat_rate: tax_rate,
      total
    };

    setSaving(true);
    const invoice = await raiseInvoice({ data });
    setSaving(false);

    if (!invoice) return;
    setFileLocation(invoice.location);
    setDueDate('');
    setMargin('');
    onRaised(invoice);
  };

  // After a successful raise, only the invoice link stays.
  if (file_location) {
    return (
      <InfoBanner
        message={
          <span>
            Invoice has been raised successfully. You can download it{' '}
            <a href={file_location} target="_blank" rel="noopener noreferrer">
              here
            </a>
            .
          </span>
        }
      />
    );
  }

  // Breakdown shown before the invoice is generated.
  if (showing_summary) {
    return (
      <div className={styles.summary}>
        <div className={styles.summaryHead}>
          {selected_plan?.title} — {selected_renewal.label}
        </div>
        <p className={styles.summarySub}>
          {selected_bracket?.label} contacts · Due {due_date}
        </p>

        <div className={styles.summaryRow}>
          <span>Plan price</span>
          <span>{money(gross_price)}</span>
        </div>
        {discount_amount > 0 && (
          <div className={cx(styles.summaryRow, styles.summaryDiscount)}>
            <span>Discount ({discount_rate}%)</span>
            <span>-{money(discount_amount)}</span>
          </div>
        )}
        <div className={styles.summaryRow}>
          <span>Margin</span>
          <span>{money(margin_value)}</span>
        </div>
        <div className={styles.summaryRow}>
          <span>Subtotal</span>
          <span>{money(subtotal)}</span>
        </div>
        <div className={styles.summaryRow}>
          <span>VAT ({tax_rate}%)</span>
          <span>{money(vat)}</span>
        </div>
        <div className={cx(styles.summaryRow, styles.summaryTotal)}>
          <span>Total</span>
          <span>{money(total)}</span>
        </div>

        <Spacer />
        <GridRow num_of_columns={2}>
          <GridColumn>
            <Button type="secondary" text="← Back" onClick={() => setShowingSummary(false)} />
          </GridColumn>
          <GridColumn>
            <Button text="Raise invoice" loading={saving} disabled={saving} onClick={submit} />
          </GridColumn>
        </GridRow>
      </div>
    );
  }

  return (
    <form>
      <GridRow num_of_columns={2}>
        <GridColumn>
          <DetailItem title="Plan" required>
            <MultiSelect
              options={modules}
              value={selected_plan}
              onChange={setSelectedPlan}
              isSorted={false}
              placeholder="Select plan"
            />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Renewal Period" required>
            <MultiSelect
              options={RENEWAL_PERIODS}
              value={selected_renewal}
              onChange={setSelectedRenewal}
              isSorted={false}
            />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={2}>
        <GridColumn>
          <DetailItem title="Contact Bracket" required>
            <MultiSelect
              options={contact_brackets}
              value={selected_bracket}
              onChange={setSelectedBracket}
              isSorted={false}
              placeholder="Select bracket"
            />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Currency" required>
            <MultiSelect
              options={currencies}
              value={selected_currency}
              onChange={setSelectedCurrency}
              placeholder="Select currency"
            />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={2}>
        <GridColumn>
          <DetailItem title="Due Date" required>
            <SimpleInput
              type="date"
              className="gm-input"
              value={due_date}
              onInput={(value) => setDueDate(value)}
            />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Margin" required>
            <SimpleInput
              type="number"
              className="gm-input"
              value={margin}
              onInput={(value) => setMargin(value)}
              error={margin_error}
            />
            <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: 'var(--neutral-dark-3)' }}>
              Amount added on top of the plan price. Minimum {currency_symbol}
              {toReadableNumber(min_margin)} (
              {Math.round(minMarginRate(selected_renewal.value, selected_plan?.tier) * 100)}% of{' '}
              {currency_symbol}
              {toReadableNumber(plan_price)}).
            </p>
          </DetailItem>
        </GridColumn>
      </GridRow>
      <Spacer />
      <GridRow num_of_columns={3}>
        <GridColumn />
        <GridColumn />
        <GridColumn>
          <Button text="Review invoice →" disabled={!is_valid} onClick={review} />
        </GridColumn>
      </GridRow>
    </form>
  );
};
